import { matchingRegion, regionLabel, regionShortLabel, shortDay } from '@remote-medium/shared';
import type { InferResponseType } from 'hono/client';

import type { admin } from '../../../lib/client.ts';

export type MatchRow = InferResponseType<
  (typeof admin.roles)[':id']['matches']['$get'],
  200
>['data']['matches'][number];

/** Where a match sits for this role. Each match is in exactly one stage. */
export type Stage = 'ready' | 'review' | 'confirmed' | 'asked' | 'introduced' | 'rejected';

export const STAGES: { value: Stage; label: string }[] = [
  { value: 'ready', label: 'Ready to introduce' },
  { value: 'review', label: 'To review' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'asked', label: 'Asked' },
  { value: 'introduced', label: 'Introduced' },
  { value: 'rejected', label: 'Rejected' },
];

export function stageOf(m: MatchRow): Stage {
  if (m.introSentAt) return 'introduced';
  if (m.candidateConsent === 'yes') return 'ready';
  if (m.candidateConsent === 'no' || m.curatorDecision === 'rejected') return 'rejected';
  if (m.curatorDecision === 'pending') return 'review';
  return m.consentRequestedAt ? 'asked' : 'confirmed';
}

/** Asked, and the consent link ran out before they answered. */
export function consentLinkExpired(m: MatchRow, now = new Date()): boolean {
  return (
    m.candidateConsent === 'pending' &&
    m.consentExpiresAt !== null &&
    new Date(m.consentExpiresAt) < now
  );
}

/** One line for the "Where it stands" column and the sheet heading. */
export function standing(m: MatchRow, stage: Stage): string {
  switch (stage) {
    case 'review':
      return `Ranked ${shortDay(m.createdAt)} · not reviewed`;
    case 'confirmed':
      return `Confirmed ${shortDay(m.decidedAt ?? m.createdAt)} · not asked`;
    case 'asked':
      return consentLinkExpired(m)
        ? `Asked ${shortDay(m.consentRequestedAt!)} · link expired`
        : `Asked ${shortDay(m.consentRequestedAt!)} · no answer yet`;
    case 'ready':
      return m.companyReaction === 'interested'
        ? 'Said yes · company interested'
        : m.companyReaction === 'pass'
          ? 'Said yes · company passed'
          : 'Said yes · company hasn’t reacted';
    case 'introduced':
      return `Introduced ${shortDay(m.introSentAt!)}`;
    case 'rejected':
      if (m.candidateConsent === 'no') return 'Said no';
      return [
        'Rejected',
        m.decidedAt && shortDay(m.decidedAt),
        `by ${m.decidedByName ?? 'a curator'}`,
      ]
        .filter(Boolean)
        .join(' ');
  }
}

/** "APAC", "Outside APAC", or the country when the role is open worldwide. */
export function regionFit(m: MatchRow, roleRegions: string[]): string {
  if (roleRegions.length === 0 || roleRegions.includes('WORLDWIDE')) return regionLabel(m.country);
  const region = matchingRegion(roleRegions, m.country);
  return region
    ? regionShortLabel(region)
    : `Outside ${roleRegions.map(regionShortLabel).join(', ')}`;
}
